'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { VisuallyHidden } from './VisuallyHidden';
import styles from './Dialog.module.css';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={`${styles.overlay} ${className || ''}`}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  // Normalize prop name casing -- Radix expects `aria-describedby` if provided via props
  // We'll prefer any passed aria-describedby; otherwise explicitly set undefined to
  // silence the Radix warning when no DialogDescription is present.
  const finalAriaDescribedBy = (props as any)['aria-describedby'] ?? undefined;

  // Helper: recursively search React children tree for a component by constructor or displayName
  const hasDescendant = (
    nodes: React.ReactNode,
    matchDisplayName: string | null,
    matchCtor?: any,
  ): boolean => {
    const arr = React.Children.toArray(nodes);
    for (const child of arr) {
      // primitives like DialogPrimitive.Title may be wrapped by our local components
      const c: any = child as any;
      if (!c) continue;
      if (c.type) {
        if (matchCtor && c.type === matchCtor) return true;
        if (matchDisplayName && c.type.displayName === matchDisplayName) return true;
      }
      if (c.props && c.props.children) {
        if (hasDescendant(c.props.children, matchDisplayName, matchCtor)) return true;
      }
    }
    return false;
  };

  const hasTitle = hasDescendant(
    children,
    DialogPrimitive.Title.displayName ?? null,
    DialogPrimitive.Title,
  );
  const hasDescription = hasDescendant(
    children,
    DialogPrimitive.Description.displayName ?? null,
    DialogPrimitive.Description,
  );

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={`${styles.content} ${className || ''}`}
        // Ensure `aria-describedby` prop exists (may be undefined) so Radix sees it explicitly
        aria-describedby={finalAriaDescribedBy}
        // If the consumer didn't provide a Title, provide an explicit aria-label
        // so assistive tech has a stable label and Radix's accessibility check is satisfied.
        aria-label={hasTitle ? undefined : ((props as any)['aria-label'] ?? 'Dialog')}
        {...props}
      >
        {!hasTitle && (
          <VisuallyHidden>
            <DialogPrimitive.Title>Dialog</DialogPrimitive.Title>
          </VisuallyHidden>
        )}

        {/* If no description element, inject a visually-hidden description to satisfy Radix accessibility checks */}
        {!hasDescription && (
          <VisuallyHidden>
            <DialogPrimitive.Description>
              This dialog provides additional information.
            </DialogPrimitive.Description>
          </VisuallyHidden>
        )}

        {children}
        <DialogPrimitive.Close className={styles.close}>
          <X className={styles.closeIcon} />
          <span className={styles.screenReaderText}>Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={`${styles.header} ${className || ''}`} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={`${styles.footer} ${className || ''}`} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={`${styles.title} ${className || ''}`} {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={`${styles.description} ${className || ''}`}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
